import * as React from "react";
import { AccessReportParametersModel } from "@app/access-reports/wizard";
import { cloneDeep, isEqual } from "lodash";
import { FlyoutFooter } from "@app/shared/Flyouts";
import { FC, JSX, useEffect, useState } from "react";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { AccessReportsWizardAccessCriteria } from "@app/access-reports/shared/accessReportsWizardAccessCriteria";
import { AccessReportsWizardAnalysisTypeEnum } from "@app/access-reports/shared/accessReportsWizardAnalysisTypeEnum";
import { AccessReportsWizardCalculationMethodEnum } from "@app/access-reports/shared/accessReportsWizardCalculationMethodEnum";
import { AccessReportsWizardNetworkStandard } from "@app/access-reports/shared/accessReportsWizardNetworkStandard";
import { AccessReportsWizardNetworkStandardTypeEnum } from "@app/access-reports/shared/accessReportsWizardNetworkStandardTypeEnum";
import { AccessReportsWizardProduct } from "@app/access-reports/shared/accessReportsWizardProduct";

interface AccessReportsWizardEditNetworksProps {
  accessCriteria: AccessReportsWizardAccessCriteria;
  accessReportParameters: AccessReportParametersModel;
  onProductChange: (accessCriteria: AccessReportsWizardAccessCriteria) => void;
  product: AccessReportsWizardProduct;
}

const AccessReportsWizardEditNetworks: FC<AccessReportsWizardEditNetworksProps> = (props: AccessReportsWizardEditNetworksProps) => {
  const [accessCriteria, setAccessCriteria] = useState<AccessReportsWizardAccessCriteria>(null);

  useEffect(() => {
    if (!!props.accessCriteria) {
      setStandards();
    }
  }, [props.accessCriteria]);

  const getSpecialtyValidity = (): boolean => {
    if (!accessCriteria) {
      return false;
    } else {
      for (const standard of accessCriteria.standards) {
        for (const value of Object.values(standard)) {
          if (!value || value?.toString() === "0" || value?.toString() === ".") {
            return false;
          }
        }
      }
    }

    return true;
  }

  const onInputChange = (
    standardType: AccessReportsWizardNetworkStandardTypeEnum,
    key: keyof AccessReportsWizardNetworkStandard,
    event?: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newAccessCriteria: AccessReportsWizardAccessCriteria = cloneDeep(accessCriteria);

    if (!!newAccessCriteria) {
      const standard: AccessReportsWizardNetworkStandard = newAccessCriteria.standards.find(x => x.type === standardType);

      if (!!standard) {
        const updateStandard = (newValue: unknown): void => {
          if (key === "miles" || key === "minCount" || key === "minutes")
            (standard)[key] = newValue as number;
          else
            standard[key] = newValue as AccessReportsWizardNetworkStandardTypeEnum;

          setAccessCriteria(newAccessCriteria);
        }

        if (!!event) {
          // on change
          const unparsedValue: string = event.target.value;
          let value: number | string = parseFloat(event.target.value);


          if (key === "miles") {
            const re = /^([0-9]{0,4}|[0-9]{0,4}\.[0-9]?)$/;

            if (re.test(unparsedValue)) {
              if (value >= 1) {
                // remove leading zeros
                value = unparsedValue.replace(/^0+/, "");
              }

              if (!isNaN(value as number) || !unparsedValue || unparsedValue === ".") {
                updateStandard((!unparsedValue || unparsedValue === ".") ? unparsedValue : value);
              }

            }
          } else {
            if ((!isNaN(value) && unparsedValue !== "0") || !unparsedValue) {
              updateStandard(!unparsedValue ? unparsedValue : value);
            }
          }
        } else {
          // on blur
          if (!(standard)[key] ||
            ((standard)[key] as number).toString() === "0" ||
            ((standard)[key] as number).toString() === "." ||
            ((standard)[key] as number).toString() === "0."
          ) {
            // assign min value
            updateStandard(key === "miles" ? .1 : 1);
          }
        }
      }
    }
  }

  const setStandards = (): void => {
    setAccessCriteria(cloneDeep(props.accessCriteria));
  }

  const renderForm = (): JSX.Element => {
    let thirdLabel = "";

    switch (props.accessReportParameters.calculationMethod.id) {
      case AccessReportsWizardCalculationMethodEnum.Distance:
        thirdLabel = "miles.";
        break;
      case AccessReportsWizardCalculationMethodEnum.Time:
        thirdLabel = "minutes.";
        break;
      case AccessReportsWizardCalculationMethodEnum.TimeAndDistance:
        thirdLabel = "miles and";
        break;
      case AccessReportsWizardCalculationMethodEnum.TimeOrDistance:
        thirdLabel = "miles or";
        break;
      default:
        break;
    }

    const buildSection = (standardType: AccessReportsWizardNetworkStandardTypeEnum): JSX.Element => {
      const getValue = (standardType: AccessReportsWizardNetworkStandardTypeEnum, key: keyof AccessReportsWizardNetworkStandard): number => {
        const standard: AccessReportsWizardNetworkStandard = accessCriteria.standards.find(x => x.type === standardType);

        if (!!standard) {
          return (standard)[key] as number;
        }
      }

      const getStandardName = (standard: string): string => {
        const regex = /[A-Z]?[a-z]+|[0-9]+|[A-Z]+(?![a-z])/g;
        return standard.match(regex).join(" ");
      }

      return (
        <>
          <div className="row">
            <div className="col-12">
              <h3 className="header-m">
                {getStandardName(standardType)}
              </h3>

              <hr className="mt-0" />
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="form-inline mb-3">
                <label className="label-s">
                  Count
                </label>
                <QESTextInput
                  className="edit-network"
                  label=""
                  testId={`access-reports-wizard__edit-networks--${standardType}-first-input`}
                  onBlur={() => onInputChange(standardType, "minCount")}
                  onChange={(e) => onInputChange(standardType, "minCount", e)}
                  value={getValue(standardType, "minCount")}
                />
                <label className="label-s">
                  in
                </label>
                <QESTextInput
                  className="edit-network"
                  label=""
                  testId={`access-reports-wizard__edit-networks--${standardType}-second-input`}
                  onBlur={() => onInputChange(standardType, props.accessReportParameters.calculationMethod.id !== AccessReportsWizardCalculationMethodEnum.Time ? "miles" : "minutes")}
                  onChange={(e) => onInputChange(standardType, props.accessReportParameters.calculationMethod.id !== AccessReportsWizardCalculationMethodEnum.Time ? "miles" : "minutes", e)}
                  value={getValue(standardType, props.accessReportParameters.calculationMethod.id !== AccessReportsWizardCalculationMethodEnum.Time ? "miles" : "minutes")}
                />
                <label className="label-s">
                  {thirdLabel}
                </label>
                {(props.accessReportParameters.calculationMethod.id === AccessReportsWizardCalculationMethodEnum.TimeAndDistance ||
                  props.accessReportParameters.calculationMethod.id === AccessReportsWizardCalculationMethodEnum.TimeOrDistance
                ) &&
                  <>
                    <QESTextInput
                      className="edit-network"
                      label=""
                      testId={`access-reports-wizard__edit-networks--${standardType}-third-input`}
                      onBlur={() => onInputChange(standardType, "minutes")}
                      onChange={(e) => onInputChange(standardType, "minutes", e)}
                      value={getValue(standardType, "minutes")}
                    />
                    <label className="label-s">
                      minutes.
                    </label>
                  </>
                }
              </div>
            </div>
          </div>
        </>
      );
    }

    switch (props.accessReportParameters.standard.id) {
      case AccessReportsWizardAnalysisTypeEnum.Standard:
        return buildSection(AccessReportsWizardNetworkStandardTypeEnum.Standard);
      case AccessReportsWizardAnalysisTypeEnum.DynamicByCountyClass:
        return (
          <>
            {buildSection(AccessReportsWizardNetworkStandardTypeEnum.CountyLarge)}
            {buildSection(AccessReportsWizardNetworkStandardTypeEnum.CountyMetro)}
            {buildSection(AccessReportsWizardNetworkStandardTypeEnum.CountyMicro)}
            {buildSection(AccessReportsWizardNetworkStandardTypeEnum.CountyRural)}
            {buildSection(AccessReportsWizardNetworkStandardTypeEnum.CountyCEAC)}
          </>
        );
      case AccessReportsWizardAnalysisTypeEnum.DynamicByZipClass:
        return (
          <>
            {buildSection(AccessReportsWizardNetworkStandardTypeEnum.ZipUrban)}
            {buildSection(AccessReportsWizardNetworkStandardTypeEnum.ZipSuburban)}
            {buildSection(AccessReportsWizardNetworkStandardTypeEnum.ZipRural)}
          </>
        );
      default:
    }
  }

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-12">
          <label className="label-s d-block mb-0">
            Product: {props.accessReportParameters.product.name}
          </label>
          <label className="label-s d-block mb-3">
            {props.accessCriteria.name}
          </label>
        </div>
      </div>

      {!!accessCriteria && renderForm()}

      <FlyoutFooter
        submitButton={
          <QESButton
            disabled={isEqual(props.accessCriteria, accessCriteria) || !getSpecialtyValidity()}
            qesTestId="access-reports-wizard-edit-networks_save"
            className="float-end mt-3"
            onClick={() => props.onProductChange(accessCriteria)}
          >
            Save
          </QESButton>
        }
      />
    </React.Fragment>
  );
}

export { AccessReportsWizardEditNetworks };
